import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import Swal from 'sweetalert2'
import { useLoading } from '../contexts/LoadingContext'
import axiosInstance from '../services/axiosInterceptor'
import {
  LB_FIELDS, LB_LABELS, CONCERN_CATEGORIES, CONCERN_STATUS, IV_ACTIONS,
  formatThaiDate, formatRoundLabel,
} from '../utils/careUtils'

const fmtDateTimeTH = (d) =>
  d ? new Date(d).toLocaleDateString('th-TH', { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const ATT_META = {
  present: { label: 'มา', color: 'text-success' },
  absent:  { label: 'ขาด', color: 'text-danger' },
  late:    { label: 'สาย', color: 'text-warning' },
  leave:   { label: 'ลา', color: 'text-info' },
}

const bmiOf = (h, w) => (h > 0 && w > 0 ? +(w / Math.pow(h / 100, 2)).toFixed(1) : null)

// รูปนักเรียน (คลิกเปิดภาพใหญ่) — แพทเทิร์นเดียวกับหน้ารายงานอื่น
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '')
const StudentAvatar = ({ photo, name, size = 56 }) => {
  const [showModal, setShowModal] = useState(false)
  const initial = name ? name.trim().charAt(0) : '?'
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null
  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 }
  return (
    <>
      {src
        ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} onClick={() => setShowModal(true)} />
        : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>}
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}

// การ์ดคะแนนเก็บรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const ScoreCard = ({ s }) => (
  <div className={`border rounded p-3 ${s.weak ? 'bg-danger-subtle' : ''}`}>
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">{s.sub_subname} {s.weak && <Badge bg="danger" className="ms-1 fs-xs">อ่อน</Badge>}</div>
      {s.missing_items > 0 && <Badge bg="warning" text="dark">ค้าง {s.missing_items}</Badge>}
    </div>
    <div className="fs-xs text-muted">{s.sub_subid} · {s.t_name}</div>
    <div className="mt-2 pt-2 border-top">
      {s.earnedPct == null ? (
        <span className="text-muted fs-sm">ยังไม่ตรวจ</span>
      ) : (
        <div className="d-flex align-items-center gap-2">
          <ProgressBar now={s.earnedPct} variant={s.earnedPct < 50 ? 'danger' : s.earnedPct < 70 ? 'warning' : 'success'}
            style={{ height: 8, flex: 1 }} />
          <span className="fs-sm fw-semibold">{s.earned}/{s.possible} ({s.earnedPct}%)</span>
        </div>
      )}
    </div>
  </div>
)

// การ์ดการเข้าเรียนรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const SubjectAttCard = ({ s }) => (
  <div className={`border rounded p-3 ${Number(s.absent_count) > 0 ? 'bg-warning-subtle' : ''}`}>
    <div className="fw-semibold">{s.sub_subname || <span className="text-muted">ไม่ระบุวิชา</span>}</div>
    <div className="d-flex gap-3 mt-2 pt-2 border-top fs-sm">
      <span className="text-success">มา {s.present_count}</span>
      <span className="text-danger fw-semibold">ขาด {s.absent_count}</span>
      <span className="text-warning">สาย {s.late_count}</span>
      <span className="text-info">ลา {s.leave_count}</span>
    </div>
  </div>
)

// เจาะรายคน — เห็นว่านักเรียนคนนี้มีปัญหาวิชาไหน เรื่องอะไร (เข้าเรียน/คะแนน/งาน/พฤติกรรม/สุขภาพ)
export default function StudentTrackingDetail() {
  const { studentID } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setLoading } = useLoading()

  const yearId = searchParams.get('yearId')
  const semester = searchParams.get('semester')
  const [data, setData] = useState(null)
  const [scoreView, setScoreView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')
  const [attView, setAttView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  // Dark-mode tables — toggle `table-dark` on every <thead> (see StudentTracking).
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : ''
  const [skin, setSkin] = useState(currentSkin)
  useEffect(() => {
    for (const t of document.getElementsByTagName('table')) {
      t.querySelectorAll('thead').forEach(th =>
        skin === 'dark' ? th.classList.add('table-dark') : th.classList.remove('table-dark'))
    }
  })

  const loadData = useCallback(() => {
    if (!studentID || !yearId) return
    const p = new URLSearchParams()
    if (semester) p.append('semester', semester)
    setLoading(true)
    axiosInstance.get(`/api/tracking/student/${studentID}/${yearId}?${p.toString()}`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [studentID, yearId, semester, setLoading])

  useEffect(() => { loadData() }, [loadData])

  // ---- ระบบดูแลช่วยเหลือ (แจ้งดูแล/การช่วยเหลือ) ----
  const [careRound, setCareRound] = useState('') // '' = รอบล่าสุดที่มีข้อมูล
  const [ivConcern, setIvConcern] = useState(null)
  const [ivForm, setIvForm] = useState({ iv_action: 'counsel', iv_detail: '', iv_outcome: '', iv_followup_date: '' })
  const [submitting, setSubmitting] = useState(false)

  const careRounds = useMemo(() => {
    const rounds = []
    for (const b of data?.care?.behavior || []) {
      if (!rounds.includes(b.round_start)) rounds.push(b.round_start)
    }
    return rounds // server จัดเรียงรอบล่าสุดมาก่อนแล้ว
  }, [data])
  const shownRound = careRound || careRounds[0] || null
  const roundBehavior = (data?.care?.behavior || []).filter(b => b.round_start === shownRound)

  const changeConcernStatus = (concern, status) => {
    const label = CONCERN_STATUS[status]?.label || status
    Swal.fire({
      title: `เปลี่ยนสถานะเป็น "${label}" ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(res => {
      if (!res.isConfirmed) return
      axiosInstance.put(`/api/care/concernStatus/${concern.concern_id}`, { status })
        .then(() => loadData())
        .catch(err => {
          Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || err.message })
        })
    })
  }

  const submitIntervention = () => {
    if (!ivConcern) return
    setSubmitting(true)
    axiosInstance.post('/api/care/intervention', {
      concern_id: ivConcern.concern_id,
      iv_action: ivForm.iv_action,
      iv_detail: ivForm.iv_detail.trim() || null,
      iv_outcome: ivForm.iv_outcome.trim() || null,
      iv_followup_date: ivForm.iv_followup_date || null,
    })
      .then(() => {
        setIvConcern(null)
        setIvForm({ iv_action: 'counsel', iv_detail: '', iv_outcome: '', iv_followup_date: '' })
        Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false })
        loadData()
      })
      .catch(err => {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || err.message })
      })
      .finally(() => setSubmitting(false))
  }

  const student = data?.student
  const conduct = data?.conduct
  const remaining = conduct?.summary?.remaining_points ?? 100

  // BMI ล่าสุด (เดือนมากสุด)
  const latestBmi = useMemo(() => {
    const rows = data?.bodyMass || []
    if (!rows.length) return null
    const latest = [...rows].sort((a, b) => (b.stmb_month || 0) - (a.stmb_month || 0))[0]
    return { ...latest, bmi: bmiOf(Number(latest.stbm_height), Number(latest.stbm_weight)) }
  }, [data])

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div className="d-flex align-items-center gap-3">
            {student && <StudentAvatar photo={student.st_photo} name={student.st_name} />}
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item">
                  <Link to="/academic/student-tracking">ระบบติดตามนักเรียน</Link>
                </li>
                <li className="breadcrumb-item active">รายบุคคล</li>
              </ol>
              <h4 className="main-title mb-0">
                {student ? student.st_name : 'ข้อมูลนักเรียน'}
                {student && <span className="text-muted fs-6 ms-2">({student.c_class || `ม.${student.c_id}`}/{student.r_no})</span>}
              </h4>
              {student && <p className="text-muted fs-sm mb-0">เลขประจำตัว {student.st_idnumber}</p>}
            </div>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(-1)}>
            <i className="ri-arrow-left-line me-1" /> กลับ
          </button>
        </div>

        {!data ? (
          <div className="text-center text-muted py-5">ไม่พบข้อมูล</div>
        ) : (
          <>
            {/* คะแนนเก็บรายวิชา — วิชาที่อ่อนไฮไลต์แดง */}
            <Card className="card-one mb-4">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">
                  <i className="ri-book-2-line me-2 text-primary" />คะแนนเก็บรายวิชา (ไม่รวมสอบปลายภาค)
                </Card.Title>
                <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${scoreView === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setScoreView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${scoreView === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setScoreView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {(data.scoreBreakdown || []).length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีคะแนนเก็บ</div>
                ) : scoreView === 'card' ? (
                  <div className="p-3">
                    <Row className="g-3">
                      {data.scoreBreakdown.map(s => (
                        <Col key={s.teach_id} xs={12} md={6} xl={4}>
                          <ScoreCard s={s} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <Table responsive hover size="sm" className="mb-0 align-middle">
                    <thead className="table-secondary">
                      <tr>
                        <th>รหัสวิชา</th>
                        <th>วิชา</th>
                        <th>ครูผู้สอน</th>
                        <th className="text-center" style={{ width: 200 }}>คะแนนเก็บ (ได้/เต็มที่ตรวจแล้ว)</th>
                        <th className="text-center" style={{ width: 90 }}>ค้างส่ง</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.scoreBreakdown.map(s => (
                        <tr key={s.teach_id} className={s.weak ? 'table-danger' : undefined}>
                          <td className="fs-sm">{s.sub_subid}</td>
                          <td>{s.sub_subname} {s.weak && <Badge bg="danger" className="ms-1 fs-xs">อ่อน</Badge>}</td>
                          <td className="fs-sm text-muted">{s.t_name}</td>
                          <td>
                            {s.earnedPct == null ? (
                              <span className="text-muted fs-sm">ยังไม่ตรวจ</span>
                            ) : (
                              <div className="d-flex align-items-center gap-2">
                                <ProgressBar
                                  now={s.earnedPct}
                                  variant={s.earnedPct < 50 ? 'danger' : s.earnedPct < 70 ? 'warning' : 'success'}
                                  style={{ height: 8, flex: 1 }}
                                />
                                <span className="fs-sm fw-semibold" style={{ width: 96 }}>
                                  {s.earned}/{s.possible} ({s.earnedPct}%)
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="text-center">
                            {s.missing_items > 0
                              ? <Badge bg="warning" text="dark">{s.missing_items}</Badge>
                              : <span className="text-muted">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {/* ปิดชั่วคราว: พฤติกรรมการเรียนรายวิชา (รอบ 2 สัปดาห์) — ครูประจำวิชาประเมิน
            <Card className="card-one mb-4">
              <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">
                  <i className="ri-survey-line me-2 text-primary" />พฤติกรรมการเรียนรายวิชา (รอบ 2 สัปดาห์)
                </Card.Title>
                {careRounds.length > 0 && (
                  <Form.Select size="sm" style={{ width: 'auto' }} value={shownRound || ''}
                    onChange={e => setCareRound(e.target.value)}>
                    {careRounds.map(r => (
                      <option key={r} value={r}>รอบ {formatRoundLabel(r)}</option>
                    ))}
                  </Form.Select>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                {roundBehavior.length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีผลประเมินพฤติกรรมการเรียน</div>
                ) : (
                  <Table responsive hover size="sm" className="mb-0 align-middle">
                    <thead className="table-secondary">
                      <tr>
                        <th>วิชา</th>
                        <th>ครูผู้สอน</th>
                        {LB_FIELDS.map(f => (
                          <th key={f} className="text-center" style={{ width: 110, fontSize: 11, lineHeight: 1.2 }}>
                            {LB_LABELS[f]}
                          </th>
                        ))}
                        <th>บันทึกจากครู</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roundBehavior.map(b => (
                        <tr key={`${b.teach_id}-${b.round_start}`}>
                          <td className="fs-sm">{b.sub_subid} {b.sub_subname}</td>
                          <td className="fs-sm text-muted">{b.t_name || '-'}</td>
                          {LB_FIELDS.map(f => {
                            const v = Number(b[f])
                            return (
                              <td key={f} className={`text-center fw-semibold ${v === 1 ? 'table-danger' : ''}`}>
                                <span className={v === 1 ? 'text-danger' : v === 3 ? 'text-success' : 'text-muted'}>
                                  {v === 1 ? 'ต้องปรับปรุง' : v === 3 ? 'ดี' : 'ปกติ'}
                                </span>
                              </td>
                            )
                          })}
                          <td className="fs-sm text-muted">{b.lb_note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
            */}

            {/* การแจ้งดูแล / การช่วยเหลือ */}
            <Card className="card-one mb-4">
              <Card.Header className="d-flex align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">
                  <i className="ri-flag-2-line me-2 text-primary" />การแจ้งดูแล / การช่วยเหลือ
                </Card.Title>
                {(data.care?.concerns || []).filter(c => c.concern_status !== 'resolved').length > 0 && (
                  <Badge bg="danger">
                    ยังไม่ยุติ {(data.care.concerns || []).filter(c => c.concern_status !== 'resolved').length}
                  </Badge>
                )}
              </Card.Header>
              <Card.Body>
                {(data.care?.concerns || []).length === 0 ? (
                  <div className="text-center text-muted py-3">ไม่มีการแจ้งดูแลในปีการศึกษานี้</div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {data.care.concerns.map(c => {
                      const status = CONCERN_STATUS[c.concern_status] || {}
                      return (
                        <div key={c.concern_id} className="border rounded p-3">
                          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                            <div>
                              <div className="fs-sm">
                                <Badge bg="secondary" className="me-1">{CONCERN_CATEGORIES[c.concern_category] || c.concern_category}</Badge>
                                <Badge bg={status.variant || 'secondary'}>{status.label || c.concern_status}</Badge>
                                {c.sub_subid && <span className="text-muted ms-2">วิชา {c.sub_subid} {c.sub_subname}</span>}
                              </div>
                              <div className="fs-sm mt-2">{c.concern_detail}</div>
                              <div className="fs-xs text-muted mt-1">
                                แจ้งเมื่อ {fmtDateTimeTH(c.created_at)}{c.created_by_name && ` โดย ${c.created_by_name}`}
                                {c.concern_status === 'resolved' && <> · ยุติเมื่อ {fmtDateTimeTH(c.resolved_at)}{c.resolved_by_name && ` โดย ${c.resolved_by_name}`}</>}
                              </div>
                            </div>
                            {c.concern_status !== 'resolved' && (
                              <div className="d-flex flex-wrap gap-2">
                                <Button variant="outline-info" size="sm" onClick={() => setIvConcern(c)}>
                                  <i className="ri-hand-heart-line me-1" />บันทึกการช่วยเหลือ
                                </Button>
                                <Button variant="outline-success" size="sm" onClick={() => changeConcernStatus(c, 'resolved')}>
                                  <i className="ri-check-double-line me-1" />ยุติเรื่อง
                                </Button>
                              </div>
                            )}
                          </div>
                          {(c.interventions || []).length > 0 && (
                            <ul className="list-unstyled mb-0 mt-3 pt-3 border-top ps-2">
                              {c.interventions.map(iv => (
                                <li key={iv.iv_id} className="mb-2 border-start ps-3">
                                  <div className="fs-sm">
                                    <Badge bg="info" className="me-2">{IV_ACTIONS[iv.iv_action] || iv.iv_action}</Badge>
                                    <span className="text-muted">{fmtDateTimeTH(iv.created_at)}</span>
                                    {iv.created_by_name && <span className="text-muted"> · โดย {iv.created_by_name}</span>}
                                  </div>
                                  {iv.iv_detail && <div className="fs-sm">{iv.iv_detail}</div>}
                                  {iv.iv_outcome && <div className="fs-sm text-success">ผล: {iv.iv_outcome}</div>}
                                  {iv.iv_followup_date && (
                                    <div className={`fs-xs ${Number(iv.iv_followup_done) ? 'text-muted text-decoration-line-through' : 'text-warning'}`}>
                                      นัดติดตามผล {formatThaiDate(iv.iv_followup_date)}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Row className="g-4">
              {/* การเข้าเรียนรายวิชา */}
              <Col lg={7}>
                <Card className="card-one h-100">
                  <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                    <Card.Title as="h6" className="mb-0">
                      <i className="ri-calendar-check-line me-2 text-primary" />การเข้าเรียนรายวิชา
                    </Card.Title>
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${attView === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setAttView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${attView === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setAttView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {(data.subjectAttendance || []).length === 0 ? (
                      <div className="text-center text-muted py-4">ยังไม่มีข้อมูลการเข้าเรียน</div>
                    ) : attView === 'card' ? (
                      <div className="p-3">
                        <Row className="g-2">
                          {data.subjectAttendance.map((s, i) => (
                            <Col key={i} xs={12}>
                              <SubjectAttCard s={s} />
                            </Col>
                          ))}
                        </Row>
                      </div>
                    ) : (
                      <Table responsive hover size="sm" className="mb-0 align-middle">
                        <thead className="table-secondary">
                          <tr>
                            <th>วิชา</th>
                            <th className="text-center" style={{ width: 60 }}>มา</th>
                            <th className="text-center" style={{ width: 60 }}>ขาด</th>
                            <th className="text-center" style={{ width: 60 }}>สาย</th>
                            <th className="text-center" style={{ width: 60 }}>ลา</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.subjectAttendance.map((s, i) => (
                            <tr key={i} className={Number(s.absent_count) > 0 ? 'table-warning' : undefined}>
                              <td className="fs-sm">{s.sub_subname || <span className="text-muted">ไม่ระบุวิชา</span>}</td>
                              <td className="text-center text-success">{s.present_count}</td>
                              <td className="text-center text-danger fw-semibold">{s.absent_count}</td>
                              <td className="text-center text-warning">{s.late_count}</td>
                              <td className="text-center text-info">{s.leave_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* พฤติกรรม + สุขภาพ */}
              <Col lg={5}>
                <Card className="card-one mb-4">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">
                      <i className="ri-shield-user-line me-2 text-primary" />พฤติกรรม
                    </Card.Title>
                    <Badge bg={remaining < 70 ? 'danger' : remaining < 90 ? 'warning' : 'success'} text={remaining < 70 || remaining >= 90 ? undefined : 'dark'}>
                      เหลือ {remaining} / 100
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {(conduct?.records || []).length === 0 ? (
                      <div className="text-center text-muted py-4">ไม่มีบันทึกการหักคะแนน</div>
                    ) : (
                      <Table responsive size="sm" className="mb-0 fs-sm align-middle">
                        <tbody>
                          {conduct.records.map(r => (
                            <tr key={r.record_id}>
                              <td>
                                {r.rule_name_snapshot}
                                {r.note && <div className="fs-xs text-muted">{r.note}</div>}
                                <div className="fs-xs text-muted">{fmtDateTimeTH(r.created_at)} · {r.created_by_name || '-'}</div>
                              </td>
                              <td className="text-end text-danger fw-semibold" style={{ width: 60 }}>-{r.deduct_points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>

                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">
                      <i className="ri-heart-pulse-line me-2 text-primary" />สุขภาพ / น้ำหนักส่วนสูง
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    {latestBmi ? (
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <div className="fs-sm text-muted">ล่าสุด (เดือน {latestBmi.stmb_month})</div>
                          <div>สูง {latestBmi.stbm_height} ซม. · หนัก {latestBmi.stbm_weight} กก.</div>
                        </div>
                        {latestBmi.bmi != null && (
                          <Badge bg={latestBmi.bmi < 18.5 || latestBmi.bmi > 25 ? 'warning' : 'success'} text={latestBmi.bmi < 18.5 || latestBmi.bmi > 25 ? 'dark' : undefined} className="ms-auto fs-sm">
                            BMI {latestBmi.bmi}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-2">ยังไม่มีข้อมูลน้ำหนักส่วนสูง</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ประวัติการเช็คชื่อล่าสุด */}
            <Card className="card-one mt-4">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">
                  <i className="ri-history-line me-2 text-primary" />ประวัติการเช็คชื่อล่าสุด (ไม่รวม "มา")
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                {(() => {
                  const nonPresent = (data.attendanceDetail || []).filter(a => a.att_status !== 'present').slice(0, 50)
                  if (nonPresent.length === 0) return <div className="text-center text-muted py-4">ไม่มีประวัติขาด/สาย/ลา</div>
                  return (
                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                      <Table responsive hover size="sm" className="mb-0 fs-sm align-middle">
                        <thead className="table-secondary sticky-top">
                          <tr>
                            <th style={{ width: 120 }}>วันที่</th>
                            <th className="text-center" style={{ width: 60 }}>คาบ</th>
                            <th>วิชา</th>
                            <th style={{ width: 80 }}>สถานะ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nonPresent.map((a, i) => {
                            const m = ATT_META[a.att_status] || { label: a.att_status, color: '' }
                            return (
                              <tr key={i}>
                                <td>{new Date(a.att_date).toLocaleDateString('th-TH', { year: '2-digit', month: 'short', day: 'numeric' })}</td>
                                <td className="text-center">{a.period_order}</td>
                                <td>{a.sub_subname || <span className="text-muted">-</span>}</td>
                                <td className={`fw-semibold ${m.color}`}>{m.label}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )
                })()}
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      {/* Modal: บันทึกการช่วยเหลือ (ครูที่ปรึกษา/ฝ่ายที่เกี่ยวข้อง) */}
      <Modal show={!!ivConcern} onHide={() => setIvConcern(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h6">บันทึกการช่วยเหลือ — {student?.st_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>การดำเนินการ</Form.Label>
            <Form.Select value={ivForm.iv_action}
              onChange={e => setIvForm(p => ({ ...p, iv_action: e.target.value }))}>
              {Object.entries(IV_ACTIONS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>รายละเอียด</Form.Label>
            <Form.Control as="textarea" rows={2} value={ivForm.iv_detail}
              onChange={e => setIvForm(p => ({ ...p, iv_detail: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>ผลลัพธ์ (ถ้ามี)</Form.Label>
            <Form.Control as="textarea" rows={2} value={ivForm.iv_outcome}
              onChange={e => setIvForm(p => ({ ...p, iv_outcome: e.target.value }))} />
          </Form.Group>
          <Form.Group>
            <Form.Label>นัดติดตามผล (ถ้ามี)</Form.Label>
            <Form.Control type="date" value={ivForm.iv_followup_date}
              onChange={e => setIvForm(p => ({ ...p, iv_followup_date: e.target.value }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setIvConcern(null)}>ยกเลิก</Button>
          <Button variant="primary" size="sm" onClick={submitIntervention} disabled={submitting}>
            {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Footer />
    </React.Fragment>
  )
}
